import {
  AIContext,
  AIDataComponent,
  AIOptions,
  AI_BOT_STATE,
  EventEmitter,
  GLOBAL_AI_BOT_STATE,
  useUserMSPCheckHook,
  withPlanFeatureGating
} from '@nile/components';
import {
  getCurrentUserHook,
  getTranslatedText
} from '@nile/components/src/Utils/helpers';
import { Button, DisplayText, TextStyle } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isEmpty } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import AINotifications from '@nile/components/src/Components/AI/components/AINotifications/AINotifications';
import AIGreetings from './components/AIGreetings';
import GlobalAIBot from './components/GlobalAIBot';
import RandomText from './components/RandomText';
import AIContainerContext from './context/AIContainerContext';
import AIOnboardingComponent from './components/AIOnboarding/components/AIOnboardingComponent';
import './styles/ai-styles.scss';

const Wrapper = ({ children, isOpen }) => {
  const containerRef = React.useRef(null);
  const aiContext = useContext(AIContext);

  useEffect(() => {
    // Function to run when height changes
    const handleHeightChange = (entries) => {
      for (let entry of entries) {
        if (entry.contentRect) {
          const newHeight = entry.contentRect.height;
          if (aiContext?.repositionPopup) {
            aiContext?.repositionPopup();
          }
        }
      }
    };

    // Create a ResizeObserver instance
    const resizeObserver = new ResizeObserver(handleHeightChange);

    // Start observing the target element
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Cleanup observer on component unmount
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const classNames = [
    'ai-container-wrapper w410 fx fcol p20 br10 text-white ai-floater-border',
    isOpen ? 'ai-active' : 'ai-inactive op-0'
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div id="AI-Container-wrapper" className={classNames} ref={containerRef}>
      <div id="ai-container-inner" className="position-relative">
        <div className="ai-container-drag-handle handle position-absolute ignoreOverride">
          <SVGIcon name="Reorder" color="white" />
        </div>
        {children}
      </div>
    </div>
  );
};

const MainComponent = ({
  loadingClassNames,
  loadingRiveState,
  customLoadingText,
  data,
  customHeaderText,
  nonLoadingClassNames,
  loading,
  setLoading,
  aiOptions,
  openAISummary,
  onboardMessage
}) => {
  const intl = useIntl();
  const [collapseAIOptions, setCollapseAIOptions] = useState(true);
  const DEFAULT_LOADING_TEXT = getTranslatedText(
    intl,
    'reports.report.filter.client.loading'
  );

  const DEFAULT_HEADER_TEXT = 'Done. Here is the translation of the text';

  return (
    <>
      {/* Loading state */}
      <div id="loading-component" className={loadingClassNames}>
        <GlobalAIBot riveState={loadingRiveState} stateMachine="Monica_Bot" />
        <div className="mt-10 tcenter">
          <RandomText
            text={
              customLoadingText || data?.loadingText || DEFAULT_LOADING_TEXT
            }
          />
        </div>
      </div>

      {/* Non - Loading state : When loading we will hide this with styles */}
      <div id="non-loading-component" className={nonLoadingClassNames}>
        {data && (
          <>
            <AIDataComponent
              data={data}
              setLoading={setLoading}
              loading={loading}
              options={aiOptions}
              headerText={
                customHeaderText || data?.successText || DEFAULT_HEADER_TEXT
              }
            />
            {data?.showAIOptions && (
              <div className="ai-dark-bg p10 br10 mt10">
                <div
                  className="flex fx-sb actr pointer"
                  onClick={() => {
                    setCollapseAIOptions((prevState) => !prevState);
                  }}
                >
                  <div>
                    <Button aiPlain plain>
                      <DisplayText element="p" size="large" weight="Medium">
                        Other actions
                      </DisplayText>
                    </Button>
                  </div>
                  <div className="ignoreOverride">
                    <SVGIcon
                      name={collapseAIOptions ? 'ArrowRight' : 'ArrowDown'}
                      color="var(--sops-ai-plain)"
                    />
                  </div>
                </div>
                {!collapseAIOptions && (
                  <div className="mt10">
                    <AIOptions options={aiOptions} onClick={openAISummary} />
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {!isEmpty(aiOptions) && !data && (
          <>
            <div className="flex g10 mb10 actr">
              <div className="ai-container-bot">
                <GlobalAIBot riveState={GLOBAL_AI_BOT_STATE.entry} />
              </div>

              <div className="ai-loading-text">
                <TextStyle>
                  <div className="text-white">
                    Here are few things I can help you with
                  </div>
                </TextStyle>
              </div>
            </div>

            <div className="ai-options-container p10 br10 ai-dark-bg">
              <AIOptions options={aiOptions} onClick={openAISummary} />
            </div>
          </>
        )}

        {onboardMessage && !data && (
          <div className="p10 br10 ai-dark-bg flex col actr jctr ai-onboard-message">
            <div className="mb-10">
              <GlobalAIBot riveState={GLOBAL_AI_BOT_STATE.entry} />
            </div>
            <div className="text-white tcenter">
              <RandomText text={onboardMessage} maintainText />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const AISummaryContainer = (props) => {
  const { setRiveState, onboardMessage } = props;
  const aiContext = useContext(AIContext);

  const [data, setData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  const [customLoadingText, setCustomLoadingText] = useState(null);

  const [customHeaderText, setCustomHeaderText] = useState(null);

  const openAISummary = (data) => {
    setData(data);
  };

  const onBack = () => {
    setData(null);
    if (isEmpty(props?.aiOptions)) {
      props?.onClose();
    } else {
      props?.onBack();
    }
  };

  const closeAISummary = () => {
    setIsOpen(false);
    setData(null);
    if (setRiveState) {
      setRiveState(AI_BOT_STATE.idle);
    }
    if (props?.onClose) {
      props?.onClose();
    }
  };

  const loadingClassNames = [
    'ai-loading-container ai-dark-bg br10',
    loading ? 'active p20 flex col actr jctr' : 'inactive'
  ]
    .filter(Boolean)
    .join(' ');

  const nonLoadingClassNames = [loading && 'hiddenActions']
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    EventEmitter.subscribe('closeAISummary', closeAISummary);
    EventEmitter.subscribe('AISummaryClosed', closeAISummary);
    EventEmitter.subscribe('tabChange', closeAISummary);
    return function cleanup() {
      EventEmitter.unsubscribe('closeAISummary');
      EventEmitter.unsubscribe('AISummaryClosed');
      EventEmitter.unsubscribe('closeAISummary');
    };
  }, []);

  useEffect(() => {
    if (props?.aiData) {
      openAISummary(props?.aiData);
    }
  }, [props?.aiData]);

  const [loadingRiveState, setLoadingRiveState] = useState(null);

  const updateLoadingRiveState = (update) => {
    if (loadingRiveState !== update) {
      setTimeout(() => {
        setLoadingRiveState(update);
      }, 100);
    }
  };

  useEffect(() => {
    if (loading) {
      updateLoadingRiveState(GLOBAL_AI_BOT_STATE.Search);
    }
  }, [loading]);

  const getComponent = () => {
    if (aiContext?.aiOnboardingHook?.isActive) {
      return (
        <div className="ai-content-container-max-height scroll hide-scroll">
          <AIOnboardingComponent />
        </div>
      );
      // return
      // <div onClick={onOnboardComplete}> sample </div>;
    }
    if (aiContext?.showGreeting) {
      return <AIGreetings />;
    }
    return (
      <>
        {isEmpty(data) && <AINotifications />}
        <MainComponent
          loadingClassNames={loadingClassNames}
          loadingRiveState={loadingRiveState}
          customLoadingText={customLoadingText}
          data={data}
          customHeaderText={customHeaderText}
          nonLoadingClassNames={nonLoadingClassNames}
          loading={loading}
          setLoading={setLoading}
          aiOptions={props?.aiOptions}
          openAISummary={openAISummary}
          onboardMessage={onboardMessage}
        />
      </>
    );
  };

  return (
    <>
      <AIContainerContext.Provider
        value={{
          onBack: onBack,
          mainLoading: loading,
          setMainLoading: setLoading,
          setCustomLoadingText: setCustomLoadingText,
          setCustomHeaderText: setCustomHeaderText
        }}
      >
        <Wrapper isOpen={isOpen}>{getComponent()}</Wrapper>
      </AIContainerContext.Provider>
    </>
  );
};

export default withPlanFeatureGating(
  function AIContainer(props) {
    const isMSP = useUserMSPCheckHook();

    if (!isMSP) return null;

    return <AISummaryContainer {...props} />;
  },
  {
    feature: 'AI_FEATURES',
    mode: 'hide',
    id: 'AIContainer'
  }
);

// export default withErrorBoundary(AISummaryContainer);
